"use client";

import { ChevronDown, MapPin, X } from "@/components/icons";
import { SimpleZipInput } from "@/components/inputs/simple-zip-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useListingBrowseContext } from "../context/listing-browse-context";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import type { GeocodeResult } from "@/lib/geocoding/geocode";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { inputVariants } from "@/components/ui/input";
import { STATE_OPTIONS } from "./secondary-filters";
import { SET_CHIP_CLASS } from "./filter-chip";

export const RADIUS_PRESETS = [
  { value: 25, label: "25 mi" },
  { value: 50, label: "50 mi" },
  { value: 100, label: "100 mi" },
  { value: 200, label: "200 mi" },
  { value: 500, label: "500 mi" },
  { value: 0, label: "Nationwide" },
] as const;

export function useLocationHandlers() {
  const heroMap = useHeroMapContext();
  const { setStateCode } = useListingBrowseContext();

  const handleZipSelect = (data: GeocodeResult, postalCode: string) => {
    setStateCode(null);
    heroMap.setLocation({
      lat: data.latitude,
      lng: data.longitude,
      postalCode,
      city: data.city,
      state: data.state,
    });
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setStateCode(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        heroMap.setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          postalCode: undefined,
          city: undefined,
          state: undefined,
        });
      },
      () => {},
    );
  };

  return {
    handleZipSelect,
    handleUseMyLocation,
    handleClear: () => heroMap.setLocation(null),
  };
}

export function useLocationLabel(): string | null {
  const heroMap = useHeroMapContext();
  const { stateCode } = useListingBrowseContext();
  const location = heroMap.location;
  const radiusMiles = heroMap.radiusMiles ?? 50;
  const radiusLabel = radiusMiles === 0 ? "nationwide" : `${radiusMiles} mi`;
  if (location?.postalCode) {
    const place = location.city ? `${location.city}, ${location.state}` : location.postalCode;
    return `${place} · ${radiusLabel}`;
  }
  if (location?.lat !== undefined) return `My location · ${radiusLabel}`;
  if (stateCode) return stateNameFor(stateCode);
  return null;
}

function stateNameFor(stateCode: string): string {
  return STATE_OPTIONS.find((option) => option.value === stateCode)?.label ?? stateCode;
}

function StatePicker() {
  const { stateCode, setStateCode } = useListingBrowseContext();
  const heroMap = useHeroMapContext();
  return (
    <div>
      <label
        htmlFor="marketplace-filter-state"
        className="mb-1.5 block text-xs font-medium text-muted-foreground"
      >
        Or a whole state
      </label>
      <select
        id="marketplace-filter-state"
        value={stateCode ?? ""}
        onChange={(event) => {
          const next = event.target.value || null;
          setStateCode(next);
          if (next) heroMap.setLocation(null);
        }}
        className={inputVariants({ size: "compact" })}
      >
        <option value="">Any state</option>
        {STATE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function LocationTrigger({ locationLabel }: { locationLabel: string | null }) {
  return (
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="outline"
        size="compact"
        className={cn("max-w-[320px] rounded-sm", locationLabel && SET_CHIP_CLASS)}
      >
        <MapPin className="text-muted-foreground" />
        <span className="text-muted-foreground">Location:</span>
        <span className={cn("truncate text-foreground", locationLabel && "font-semibold")}>
          {locationLabel ?? "Anywhere"}
        </span>
        <ChevronDown className="ml-auto text-muted-foreground" />
      </Button>
    </PopoverTrigger>
  );
}

function ZipField({ clearable }: { clearable: boolean }) {
  const heroMap = useHeroMapContext();
  const { setStateCode } = useListingBrowseContext();
  const { handleZipSelect, handleClear } = useLocationHandlers();
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1.5">Near a ZIP code</div>
      <div className="relative">
        <SimpleZipInput
          value={heroMap.location?.postalCode ?? ""}
          onChange={() => {}}
          onZipSelect={handleZipSelect}
          placeholder="Enter ZIP code"
          className={cn(
            "h-9 w-full px-3",
            "text-sm border border-border rounded-sm bg-card",
            "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]",
          )}
          showValidIndicator={false}
        />
        {clearable && (
          <IconButton
            variant="quiet"
            size="inline"
            onClick={() => {
              handleClear();
              setStateCode(null);
            }}
            aria-label="Clear location"
            className="absolute right-2 top-1/2 -translate-y-1/2"
          >
            <X />
          </IconButton>
        )}
      </div>
    </div>
  );
}

function RadiusPicker() {
  const heroMap = useHeroMapContext();
  if (heroMap.location?.lat === undefined) return null;
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-1.5">Search radius</div>
      <SegmentedControl
        size="compact"
        aria-label="Search radius"
        value={String(heroMap.radiusMiles ?? 50)}
        onValueChange={(next) => heroMap.setRadiusMiles(Number(next))}
        options={RADIUS_PRESETS.map((preset) => ({
          value: String(preset.value),
          label: preset.label,
        }))}
      />
    </div>
  );
}

export function LocationFilter() {
  const { handleUseMyLocation } = useLocationHandlers();
  const locationLabel = useLocationLabel();
  return (
    <Popover>
      <LocationTrigger locationLabel={locationLabel} />
      <PopoverContent align="start" className="w-[280px] p-3 rounded-sm border-border">
        <div className="space-y-3">
          <ZipField clearable={locationLabel !== null} />
          <Button
            type="button"
            variant="outline"
            size="compact"
            onClick={handleUseMyLocation}
            className="w-full rounded-sm"
          >
            <MapPin />
            Use my location
          </Button>
          <RadiusPicker />
          <StatePicker />
        </div>
      </PopoverContent>
    </Popover>
  );
}
